import type { RatingBoard } from '@brush/api-client';
import { describeApiError } from '@brush/api-client';
import {
  BuilderSection,
  ChoiceList,
  ErrorNotice,
  IssueList,
  Labelled,
  NumberInput,
  Pill,
  Spinner,
  controlClass,
  inputClass,
  latestPublished,
  readDefinition,
  readScope,
  scopeKey,
  scopeRangeLabel,
  tagVocabularyFromUsage,
  toScopeDocument,
  useDebouncedValue,
  type PointsScope,
} from '@brush/ui';
import { AlertTriangle, ArrowLeft, Lock } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  usePlatformTags,
  usePointsSystemVersion,
  usePointsSystems,
  useRatingStandings,
  useScopeCandidates,
  useScopeEvents,
  useTournamentTagUsage,
  useUpdateRatingBoard,
} from '../../queries';
import {
  COHORT_DISCLOSURE,
  VISIBILITY_ORDER,
  aggregationNotice,
  effectiveRangeNotice,
  participationNotice,
  periodAnchor,
  readAggregation,
  readCohort,
  readQualification,
  readVisibility,
  readWindow,
  sectionLock,
  tiebreakerProblem,
  toAggregationDocument,
  toCohortDocument,
  toQualificationDocument,
  toWindowDocument,
  validateBoard,
  visibilityConfirmation,
  type BoardSection,
  type RatingAggregationDraft,
  type RatingCohortDraft,
  type RatingQualificationDraft,
  type RatingVisibility,
  type RatingWindow,
} from '../../ratings/builder';
import { useUnsaved, useUnsavedWork } from '../../unsaved';
import { candidateParams } from '../../leaderboards/candidates';
import { candidateList } from '../../leaderboards/candidateList';
import { LeaderboardScopeFilters } from '../leaderboards/LeaderboardScopeFilters';
import { Modal } from '../Modal';
import { RatingPeriodTable } from './RatingPeriodTable';
import { RatingPreviewPane } from './RatingPreviewPane';

/**
 * §9.2's rating board builder. Eight sections in §9.2's order, each collapsible with a summary
 * line, with a live preview throughout.
 *
 * <b>The preview is half of what the screen is for</b>, exactly as it is in the points designer:
 * an operator cannot reason about a decay ramp without seeing what it does to real players. A ramp
 * is five numbers between 0 and 1 and every arrangement of them looks reasonable; what tells you
 * 0.5 is too steep is watching a player who has not entered anything in eighteen months fall out of
 * the top twenty.
 *
 * <b>The scope section is `LeaderboardScopeFilters`, unchanged.</b> §9.2 requires the leaderboard's
 * editor reused rather than a second one grown, and the reason is that two scope editors over one
 * document disagree within a release — a quantifier here, an exclusion mapping there. It is used as
 * it stands, which the Phase 6 mirror move is what made possible: it filters candidates through
 * `withinScopeDates`, and before both bounds could be null a rating scope with open dates dropped
 * every event on screen.
 *
 * <b>What it does NOT do is hold the lifecycle.</b> Start, Close, Recalculate and the cadence are
 * transitions with their own routes and their own refusals, and they live on the standings screen
 * beside the figures they move. §9.9 drew the same line for a leaderboard: a field saves with the
 * form, a transition acts at once, and putting the two on one button is how Save appears to do
 * something Save does not send.
 */
export function RatingBoardBuilder({
  board,
  roomId,
  onBack,
}: {
  board: RatingBoard;
  roomId: string;
  onBack: () => void;
}) {
  const update = useUpdateRatingBoard(roomId);

  // Read once, so the stand-in date below cannot move under the operator mid-edit.
  const openedAt = useMemo(() => new Date(), []);

  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description ?? '');
  const [scope, setScope] = useState<PointsScope>(() => readScope(board.scope) ?? blankRatingScope());
  const [cohort, setCohort] = useState<RatingCohortDraft>(() => readCohort(board.cohort));
  const [window, setWindow] = useState<RatingWindow>(() => readWindow(board.window));
  const [aggregation, setAggregation] = useState<RatingAggregationDraft>(() =>
    readAggregation(board.aggregation),
  );
  const [qualification, setQualification] = useState<RatingQualificationDraft>(() =>
    readQualification(board.qualification),
  );
  const [visibility, setVisibility] = useState<RatingVisibility>(() => readVisibility(board.visibility));
  const [systemId, setSystemId] = useState(board.pointsSystemId ?? '');
  const [versionNumber, setVersionNumber] = useState<number | null>(board.pointsSystemVersionNumber);

  const [open, setOpen] = useState<BoardSection | null>('name');
  const [confirming, setConfirming] = useState<{ title: string; body: string } | null>(null);

  const issues = validateBoard({ name, window, aggregation, qualification });
  const lock = (section: BoardSection) => sectionLock(section, board);

  const outgoing = {
    name: name.trim(),
    description: description.trim() === '' ? null : description.trim(),
    pointsSystemId: systemId === '' ? null : systemId,
    pointsSystemVersion: systemId === '' ? null : versionNumber,
    scope: toScopeDocument(scope),
    cohort: toCohortDocument(cohort),
    window: toWindowDocument(window),
    aggregation: toAggregationDocument(aggregation, window),
    qualification: toQualificationDocument(qualification),
    visibility,
    // Not this screen's to set: §2.2's cadence is a transition with its own route, and a PUT
    // carrying one the form never showed would silently overwrite whatever the recalculation panel
    // had been used to choose. Sent back exactly as the board holds it.
    recalcMode: board.recalcMode,
    recalcCron: board.recalcCron,
  };

  /*
   * Dirty is DERIVED from draft-versus-stored rather than tracked, the shape the template editor
   * and the points builder both settled on: a successful save clears it with nothing having to
   * remember the save happened, because the board coming back from the server is what it is
   * compared against.
   *
   * Compared as serialised documents, which is the honest comparison here — these five blocks
   * travel as opaque JSON, and every one of them goes through the same `to*Document` the save uses,
   * so "differs" means exactly "the server would store something else".
   */
  const dirty = JSON.stringify(outgoing) !== JSON.stringify(storedOf(board));

  /*
   * Declared to room-web's CENTRAL unsaved-work guard rather than guarded here, which is what makes
   * every way out of this screen ask the same question once: the section nav, the room switcher,
   * the role switcher, closing the tab, and this screen's own back button — all of them route
   * through `leave()`. An editor with its own prompt covers the one exit its author thought of.
   */
  useUnsavedWork(dirty);
  const { leave } = useUnsaved();

  // No anchor is sent: the server decides "today" in the room's own zone, and every date on this
  // screen is then taken from what it answered. See periodAnchor.
  const standings = useRatingStandings(board.id);
  const asOf = periodAnchor(standings.data?.asOfDate, openedAt);
  const playerCount = standings.data?.data.length ?? null;

  const save = () => {
    const confirmation = visibilityConfirmation(
      readVisibility(board.visibility),
      visibility,
      name.trim() || board.name,
      playerCount,
    );

    // §4.7: publishing into Players or Public publishes a room's assessment of named individuals,
    // so the transition into either is confirmed with the count in front of the operator. Moving
    // down is silent — narrowing takes something back, and a confirmation on every change is how an
    // operator learns to click through the one that matters.
    if (confirmation !== null) return setConfirming(confirmation);
    update.mutate({ id: board.id, body: outgoing });
  };

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={() => leave(onBack)}
        className="mb-6 flex items-center gap-2 text-sm text-ink-800/60 hover:text-ink-950"
      >
        <ArrowLeft className="size-4" /> Back to ratings
      </button>

      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-display text-2xl font-semibold text-ink-950">{board.name}</h1>
        <Pill tone={board.status === 'running' ? 'good' : board.status === 'closed' ? 'neutral' : 'warn'}>
          {board.status === 'running' ? 'Running' : board.status === 'closed' ? 'Closed' : 'Draft'}
        </Pill>
        {board.isDefault ? <Pill>Room default</Pill> : null}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="min-w-0 space-y-6">
          <Section
            step={1}
            section="name"
            title="Name and description"
            summary={name || 'Unnamed rating'}
            open={open}
            setOpen={setOpen}
            lock={lock('name')}
          >
            <div className="space-y-3">
              <Labelled label="Name">
                <input
                  value={name}
                  disabled={lock('name').locked}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                />
              </Labelled>
              <Labelled label="Description" hint="What this rating is for, in a sentence.">
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputClass}
                />
              </Labelled>
              <IssueList issues={issues.filter((i) => i.section === 'name')} />
            </div>
          </Section>

          <PointsSystemSection
            step={2}
            board={board}
            roomId={roomId}
            systemId={systemId}
            versionNumber={versionNumber}
            onSelect={(id, version) => {
              setSystemId(id);
              setVersionNumber(version);
            }}
            open={open}
            setOpen={setOpen}
            lock={lock('pointsSystem')}
          />

          <ScopeSection
            step={3}
            roomId={roomId}
            scope={scope}
            onChange={setScope}
            window={window}
            asOf={asOf}
            open={open}
            setOpen={setOpen}
            lock={lock('scope')}
          />

          <CohortSection
            step={4}
            cohort={cohort}
            onChange={setCohort}
            standings={standings.data ?? null}
            open={open}
            setOpen={setOpen}
            lock={lock('cohort')}
          />

          <Section
            step={5}
            section="window"
            title="How far back"
            summary={windowSummary(window)}
            open={open}
            setOpen={setOpen}
            lock={lock('window')}
          >
            <WindowEditor
              window={window}
              aggregation={aggregation}
              onChange={(next) => {
                setWindow(next.window);
                setAggregation(next.aggregation);
              }}
              issues={issues.filter((i) => i.section === 'window')}
            />
          </Section>

          <Section
            step={6}
            section="aggregation"
            title="What counts"
            summary={aggregationSummary(aggregation)}
            open={open}
            setOpen={setOpen}
            lock={lock('aggregation')}
          >
            <div className="space-y-4">
              <RatingPeriodTable
                window={window}
                aggregation={aggregation}
                asOf={asOf}
                readOnly={lock('aggregation').locked}
                onChange={(next) => {
                  setWindow(next.window);
                  setAggregation(next.aggregation);
                }}
              />
              <IssueList issues={issues.filter((i) => i.section === 'aggregation')} />
            </div>
          </Section>

          <Section
            step={7}
            section="qualification"
            title="Minimum to be rated"
            summary={qualificationSummary(qualification)}
            open={open}
            setOpen={setOpen}
            lock={lock('qualification')}
          >
            <QualificationEditor
              qualification={qualification}
              onChange={setQualification}
              issues={issues.filter((i) => i.section === 'qualification')}
            />
          </Section>

          <Section
            step={8}
            section="visibility"
            title="Who can see it"
            summary={VISIBILITY_COPY[visibility].label}
            open={open}
            setOpen={setOpen}
            lock={lock('visibility')}
          >
            <div className="space-y-3">
              <ChoiceList
                name="rating-visibility"
                value={visibility}
                onChange={setVisibility}
                options={VISIBILITY_ORDER.map((value) => ({
                  value,
                  label: VISIBILITY_COPY[value].label,
                  detail: VISIBILITY_COPY[value].detail,
                }))}
              />
              {board.status === 'draft' ? (
                // §4.7: a Draft board reads as Private regardless of the stored value, so the
                // setting an operator is choosing here is the one that takes effect when they
                // start it. Said, rather than left to be discovered by a player who cannot find a
                // board the screen says is public.
                <p className="text-[11.5px] leading-relaxed text-ink-800/55">
                  While this rating is a draft it stays private whatever is chosen here — this is
                  what it becomes when you start it.
                </p>
              ) : null}
            </div>
          </Section>
        </div>

        <RatingPreviewPane
          board={board}
          standings={standings}
          asOf={asOf}
          /* Only for the refusal hint. The pane's FIGURES and the §4.6 rule applied to them both
             come off the computation — passing the draft's qualification here is what made one
             control in section 7 live while the one above it was not. */
          draftPointsSystemId={systemId === '' ? null : systemId}
        />
      </div>

      {/* The footer follows the template editor's: a bar that says what is unsaved and one Save. */}
      <div className="sticky bottom-0 mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-ink-800/10 bg-white/95 py-3 backdrop-blur">
        <span className="text-xs text-ink-800/60">
          {issues.length > 0
            ? `${issues.length} thing${issues.length === 1 ? '' : 's'} to fix before this can be saved.`
            : dirty
              ? 'Unsaved changes.'
              : 'Everything here is saved.'}
        </span>
        <div className="flex items-center gap-3">
          <ErrorNotice message={describeApiError(update.error)} />
          <button
            type="button"
            disabled={!dirty || issues.length > 0 || update.isPending}
            onClick={save}
            className="rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-40"
          >
            {update.isPending ? 'Saving…' : 'Save rating'}
          </button>
        </div>
      </div>

      {confirming ? (
        <Modal title={confirming.title} onClose={() => setConfirming(null)}>
          <p className="text-sm leading-relaxed text-ink-800/80">{confirming.body}</p>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="rounded-lg border border-ink-800/20 px-4 py-2 text-sm text-ink-800/70 hover:bg-ink-800/5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(null);
                update.mutate({ id: board.id, body: outgoing });
              }}
              className="rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900"
            >
              Publish it
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- one collapsible section

/**
 * §9.2's collapsible section, plus §2.6's lock rendered <b>as a disabled control carrying its
 * reason</b> rather than as a missing one.
 *
 * A control that vanishes says the feature does not exist. A greyed one with no explanation says
 * "not now" where the truth is "never, and here is why" — which is the call §9.9 already made for a
 * pinned points system, in as many words: do not disable the control silently.
 */
function Section({
  step,
  section,
  title,
  summary,
  open,
  setOpen,
  lock,
  children,
}: {
  step: number;
  section: BoardSection;
  title: string;
  summary: string;
  open: BoardSection | null;
  setOpen: (next: BoardSection | null) => void;
  lock: { locked: boolean; reason: string | null };
  children: React.ReactNode;
}) {
  return (
    <BuilderSection
      step={step}
      title={title}
      summary={summary}
      open={open === section}
      onToggle={() => setOpen(open === section ? null : section)}
      badge={
        lock.locked ? (
          <span className="inline-flex items-center gap-1 align-middle text-[10px] font-medium text-ink-800/50">
            <Lock className="size-3" /> Locked
          </span>
        ) : null
      }
    >
      <div className="space-y-3">
        {lock.locked ? (
          <p className="border-l-2 border-ink-800/20 bg-ink-800/[0.03] py-2 pl-3 text-[11.5px] leading-relaxed text-ink-800/70">
            {lock.reason}
          </p>
        ) : null}
        <fieldset disabled={lock.locked} className="min-w-0 space-y-3 disabled:opacity-60">
          {children}
        </fieldset>
      </div>
    </BuilderSection>
  );
}

// ---------------------------------------------------------------- 2. the points system

/**
 * §9.2's picker, and <b>§9.4's required notice beside it</b>.
 *
 * §9.4 is not optional and §1.3 says why: the rating engine does not read `definition.aggregation`,
 * so an operator who configured a top-15 system finds ratings quietly ignoring it. §1.3 records the
 * mitigation as this notice rather than a change to the rule, which makes the notice the whole of
 * what stands between the design and a trap.
 */
function PointsSystemSection({
  step,
  board,
  roomId,
  systemId,
  versionNumber,
  onSelect,
  open,
  setOpen,
  lock,
}: {
  step: number;
  board: RatingBoard;
  roomId: string;
  systemId: string;
  versionNumber: number | null;
  onSelect: (systemId: string, versionNumber: number | null) => void;
  open: BoardSection | null;
  setOpen: (next: BoardSection | null) => void;
  lock: { locked: boolean; reason: string | null };
}) {
  const systems = usePointsSystems(roomId, false);
  const chosen = (systems.data ?? []).find((s) => s.id === systemId) ?? null;

  const published = useMemo(
    () =>
      (chosen?.versions ?? [])
        .filter((v) => v.status === 'published')
        .sort((a, b) => b.versionNumber - a.versionNumber),
    [chosen],
  );

  const version = versionNumber ?? published[0]?.versionNumber ?? null;
  const definition = usePointsSystemVersion(systemId === '' ? null : systemId, version);
  const parsed = definition.data ? readDefinition(definition.data.definition) : null;

  const notices = [
    // §9.4 first: it is about the block the operator is looking at.
    aggregationNotice(parsed?.aggregation ?? null),
    // §1.3's hard refusal, which the server answers with a 400 after five other blocks are filled.
    tiebreakerProblem(parsed?.tiebreakers ?? null),
    // §12's participation hazard, amplified by the rolling window.
    participationNotice(parsed?.participation.mode ?? null),
  ].filter((n) => n !== null);

  return (
    <Section
      step={step}
      section="pointsSystem"
      title="Points system"
      summary={chosen ? `${chosen.name} v${version ?? '—'}` : 'Nothing selected'}
      open={open}
      setOpen={setOpen}
      lock={lock}
    >
      {systems.isLoading ? (
        <Spinner label="Loading points systems…" />
      ) : (
        <div className="space-y-3">
          <p className="text-[11.5px] leading-relaxed text-ink-800/60">
            What a finish is worth. A rating is a projection over this — the system decides what a
            result scores, and everything below decides which results count and how they age.
          </p>

          <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Labelled label="System">
              <select
                value={systemId}
                disabled={lock.locked}
                onChange={(e) => onSelect(e.target.value, null)}
                className={inputClass}
              >
                <option value="">Choose a points system…</option>
                {(systems.data ?? []).map((system) => {
                  const newest = latestPublished(system);
                  return (
                    <option key={system.id} value={system.id} disabled={!newest}>
                      {system.name}
                      {system.isPlatform ? ' (platform)' : ''}
                      {newest ? '' : ' — nothing published yet'}
                    </option>
                  );
                })}
              </select>
            </Labelled>

            <Labelled label="Version">
              <select
                value={version ?? ''}
                disabled={lock.locked || published.length === 0}
                onChange={(e) => onSelect(systemId, Number.parseInt(e.target.value, 10))}
                className={inputClass}
              >
                {published.length === 0 ? <option value="">—</option> : null}
                {published.map((v, index) => (
                  <option key={v.versionNumber} value={v.versionNumber}>
                    Version {v.versionNumber}
                    {index === 0 ? ' (latest)' : ''}
                  </option>
                ))}
              </select>
            </Labelled>
          </div>

          {notices.map((notice) => (
            <p
              key={notice.message}
              className={`flex gap-2 border p-2.5 text-[11.5px] leading-relaxed ${
                notice.severity === 'error'
                  ? 'border-chip-500/40 bg-chip-500/5 text-ink-900'
                  : 'border-gold-500/40 bg-gold-500/5 text-ink-900'
              }`}
            >
              <AlertTriangle
                className={`mt-px size-3.5 shrink-0 ${
                  notice.severity === 'error' ? 'text-chip-500' : 'text-gold-500'
                }`}
              />
              <span>{notice.message}</span>
            </p>
          ))}

          {board.status === 'draft' ? (
            <p className="text-[11.5px] leading-relaxed text-ink-800/55">
              Starting this rating pins the version, and the pin never changes afterwards — every
              standing it publishes will have been computed with it.
            </p>
          ) : null}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- 3. which events

/**
 * §9.2's third section: <b>`LeaderboardScopeFilters`, reused rather than reimplemented.</b>
 *
 * §9.2 requires it, and the reason is that two scope editors over one document disagree within a
 * release. What differs here is only the surroundings: a leaderboard supplies its scope dates from
 * its own Start and End columns, and a rating board has none — its bounds are the scope's own, and
 * usually open, because the window is what decides how far back a rating looks.
 *
 * <b>`minFieldSize` is a control here where §9.9 pins it to 0.</b> §4.2 makes 32 the sensible
 * default for a rating (it is GPI's qualifying threshold) where 4 is right for a leaderboard. The
 * cost is stated rather than hidden: it is the one §3.2 rule the browser cannot evaluate — the
 * server counts re-entries the tournament list does not report — so the candidate list below can
 * show events the rating will not score, and the live count is the authority.
 */
function ScopeSection({
  step,
  roomId,
  scope,
  onChange,
  window,
  asOf,
  open,
  setOpen,
  lock,
}: {
  step: number;
  roomId: string;
  scope: PointsScope;
  onChange: (next: PointsScope) => void;
  window: RatingWindow;
  asOf: string;
  open: BoardSection | null;
  setOpen: (next: BoardSection | null) => void;
  lock: { locked: boolean; reason: string | null };
}) {
  const platformTags = usePlatformTags();
  const tagUsage = useTournamentTagUsage(roomId);

  const debounced = useDebouncedValue(scope, 800);
  const events = useScopeEvents({ roomId, scope: toScopeDocument(debounced), enabled: true });
  // The tick list follows the same debounced scope, read page after page: with open bounds that is the whole
  // room, however many tournaments it holds (known gap 35).
  const candidates = candidateList(
    useScopeCandidates(roomId, candidateParams(debounced)),
    scope,
    debounced,
  );

  // Withheld while it lags the controls, as §9.9's count is: printing the previous number beside a
  // filter the operator has already changed states a fact about a scope that no longer exists, and
  // the count is the whole feedback loop.
  const settled = scopeKey(debounced) === scopeKey(scope) && !events.isFetching;
  const count = settled ? events.data?.eventsInScope ?? null : null;

  const tags = useMemo(
    () => tagVocabularyFromUsage(tagUsage.data ?? [], platformTags.data ?? []),
    [tagUsage.data, platformTags.data],
  );

  const intersection = effectiveRangeNotice(scope, window, asOf);

  return (
    <Section
      step={step}
      section="scope"
      title="Which events"
      summary={
        count === null
          ? scopeRangeLabel(scope)
          : `${count} event${count === 1 ? '' : 's'} · ${scopeRangeLabel(scope)}`
      }
      open={open}
      setOpen={setOpen}
      lock={lock}
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Labelled label="From" hint="Blank lets the window decide.">
            <input
              type="date"
              value={scope.startDate ?? ''}
              onChange={(e) => onChange({ ...scope, startDate: e.target.value || null })}
              className={`${controlClass} w-full`}
            />
          </Labelled>
          <Labelled label="To" hint="Blank lets the window decide.">
            <input
              type="date"
              value={scope.endDate ?? ''}
              onChange={(e) => onChange({ ...scope, endDate: e.target.value || null })}
              className={`${controlClass} w-full`}
            />
          </Labelled>
          <Labelled
            label="Smallest field"
            hint="Events with fewer entries are left out. 32 is GPI’s threshold."
          >
            <NumberInput
              value={scope.minFieldSize}
              onChange={(n) => onChange({ ...scope, minFieldSize: n ?? 0 })}
              ariaLabel="Smallest field that counts"
              min={0}
              step={1}
              className="w-full"
            />
          </Labelled>
        </div>

        {/* §4.2 requires the effective range shown wherever a board carries both, because an
            operator who sets both and sees fewer events than expected will not guess why. */}
        <IssueList issues={intersection ? [intersection] : []} />

        <LeaderboardScopeFilters
          scope={scope}
          onChange={onChange}
          candidates={candidates}
          tags={tags}
          readOnly={lock.locked}
          subject="rating"
        />

        {scope.minFieldSize > 0 ? (
          <p className="text-[11px] leading-relaxed text-ink-800/50">
            The list above cannot apply the smallest-field rule — the server counts re-entries this
            screen does not see — so some events shown may not score. The count in the heading is
            the server’s and is the one to trust.
          </p>
        ) : null}

        <ErrorNotice message={describeApiError(events.error)} />
        {/* The count never retries and is cached per scope, so a refusal (a rate limit above all) would otherwise
            stand until the scope changed (#222). */}
        {events.isError ? (
          <button
            type="button"
            onClick={() => void events.refetch()}
            disabled={events.isFetching}
            className="text-[11.5px] text-chip-500 underline underline-offset-2 disabled:opacity-50"
          >
            Try again
          </button>
        ) : null}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- 4. which players

/**
 * §9.2's fourth section, with <b>§9.6's disclosure, which is required and is not a tooltip</b>.
 *
 * §4.3 makes demographics read the Player record rather than the result row, so a player who
 * updates their card moves cohorts and their full history moves with them. That is intended — a
 * rating is about the person now — and it is surprising enough that §9.6 puts it on the screen in
 * as many words, because an operator who meets it first in a support ticket will not believe it.
 */
function CohortSection({
  step,
  cohort,
  onChange,
  standings,
  open,
  setOpen,
  lock,
}: {
  step: number;
  cohort: RatingCohortDraft;
  onChange: (next: RatingCohortDraft) => void;
  standings: { excludedAsUnknown: number; excludedFromCohort: number; data: unknown[] } | null;
  open: BoardSection | null;
  setOpen: (next: BoardSection | null) => void;
  lock: { locked: boolean; reason: string | null };
}) {
  const constrained = cohort.city.length + cohort.state.length + cohort.country.length > 0;

  return (
    <Section
      step={step}
      section="cohort"
      title="Which players"
      summary={cohortSummary(cohort, standings?.data.length ?? null)}
      open={open}
      setOpen={setOpen}
      lock={lock}
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          {(['city', 'state', 'country'] as const).map((field) => (
            <Labelled
              key={field}
              label={field === 'city' ? 'City' : field === 'state' ? 'State' : 'Country'}
              hint="Comma separated. Blank means no limit."
            >
              <input
                value={cohort[field].join(', ')}
                onChange={(e) =>
                  onChange({ ...cohort, [field]: e.target.value.split(',').map((v) => v.trim()) })
                }
                className={inputClass}
              />
            </Labelled>
          ))}
        </div>

        <p className="text-[11px] leading-relaxed text-ink-800/50">
          A blank box is no limit on that field, never a match on nothing. Fields combine: state
          “FL, GA” with city “Miami” means <em>in Miami, and in Florida or Georgia</em>.
        </p>

        {/*
          §4.3's policy, shown only when it can do anything. It governs a player whose CONSTRAINED
          field is blank, so on a cohort constraining nothing it is inert either way — and a control
          that cannot change the answer is the furniture a switcher with one useful position always
          is.
        */}
        {constrained ? (
          <ChoiceList
            name="cohort-unknown-policy"
            value={cohort.unknownPolicy}
            onChange={(unknownPolicy) => onChange({ ...cohort, unknownPolicy })}
            options={[
              {
                value: 'exclude',
                label: 'Leave out players whose card does not say',
                detail:
                  'The safe answer and the default. A room holding no city for somebody has not said they are from here.',
              },
              {
                value: 'include',
                label: 'Include them anyway',
                detail:
                  'Rates everybody the room has no details for as though they matched. Asserts something the card does not say.',
              },
            ]}
          />
        ) : null}

        {/* §9.6, in full and never behind a tooltip. */}
        <p className="border-l-2 border-gold-500/40 py-1 pl-3 text-[11.5px] leading-relaxed text-ink-800/70">
          {COHORT_DISCLOSURE}
        </p>

        {/* §4.3 and §9.5: a silent exclusion is how a player disappears and nobody can explain it. */}
        {standings && (standings.excludedAsUnknown > 0 || standings.excludedFromCohort > 0) ? (
          <p className="text-[11.5px] leading-relaxed text-ink-800/60">
            As it stands this leaves out{' '}
            <strong>{standings.excludedAsUnknown}</strong> player
            {standings.excludedAsUnknown === 1 ? '' : 's'} whose card has no city or state, and{' '}
            <strong>{standings.excludedFromCohort}</strong> outside the cohort.
          </p>
        ) : null}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- 5 and 7, the small editors

function WindowEditor({
  window,
  aggregation,
  onChange,
  issues,
}: {
  window: RatingWindow;
  aggregation: RatingAggregationDraft;
  onChange: (next: { window: RatingWindow; aggregation: RatingAggregationDraft }) => void;
  issues: readonly { severity: 'error' | 'warning'; message: string }[];
}) {
  return (
    <div className="space-y-3">
      <ChoiceList
        name="rating-window-type"
        value={window.type}
        onChange={(type) => onChange({ window: { ...window, type }, aggregation })}
        options={[
          {
            value: 'rolling',
            label: 'A rolling window, moving with the calendar',
            detail:
              'The last N months, measured from today. This is what makes a rating a rating: it moves on its own as results age out, even on a day nobody played.',
          },
          {
            value: 'fixed',
            label: 'A fixed range of dates',
            detail: 'For an annual award. The window does not move, so the rating stops changing once the range is over.',
          },
          {
            value: 'allTime',
            label: 'Everything, ever',
            detail: 'No lower bound at all. Closer to a lifetime points table than to a rating.',
          },
        ]}
      />

      {window.type === 'rolling' ? (
        <div className="flex flex-wrap items-end gap-3">
          <Labelled label="Look back" className="max-w-[8rem]">
            <NumberInput
              value={window.lengthMonths}
              onChange={(lengthMonths) => onChange({ window: { ...window, lengthMonths }, aggregation })}
              ariaLabel="Months to look back"
              min={1}
              step={1}
              className="w-full"
            />
          </Labelled>
          <span className="pb-2 text-xs text-ink-800/55">months</span>
        </div>
      ) : window.type === 'fixed' ? (
        // Fixed widths and a wrapping row: a native date box has a min-content width around 150px
        // and cannot usefully shrink, so two of them in flex slots clip and land their labels on
        // top of each other.
        <div className="grid gap-3 sm:grid-cols-2">
          <Labelled label="First day">
            <input
              type="date"
              value={window.startDate ?? ''}
              onChange={(e) =>
                onChange({ window: { ...window, startDate: e.target.value || null }, aggregation })
              }
              className={`${controlClass} w-full`}
            />
          </Labelled>
          <Labelled label="Last day" hint="Both days are included.">
            <input
              type="date"
              value={window.endDate ?? ''}
              onChange={(e) =>
                onChange({ window: { ...window, endDate: e.target.value || null }, aggregation })
              }
              className={`${controlClass} w-full`}
            />
          </Labelled>
        </div>
      ) : null}

      <IssueList issues={issues} />
    </div>
  );
}

function QualificationEditor({
  qualification,
  onChange,
  issues,
}: {
  qualification: RatingQualificationDraft;
  onChange: (next: RatingQualificationDraft) => void;
  issues: readonly { severity: 'error' | 'warning'; message: string }[];
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4">
        <Labelled label="Events entered" className="max-w-[8rem]">
          <NumberInput
            value={qualification.minEvents}
            onChange={(minEvents) => onChange({ ...qualification, minEvents })}
            ariaLabel="Minimum events entered"
            min={0}
            step={1}
            className="w-full"
          />
        </Labelled>
        <Labelled label="Cashes" className="max-w-[8rem]">
          <NumberInput
            value={qualification.minCashes}
            onChange={(minCashes) => onChange({ ...qualification, minCashes })}
            ariaLabel="Minimum cashes"
            min={0}
            step={1}
            className="w-full"
          />
        </Labelled>
      </div>

      <p className="text-[11px] leading-relaxed text-ink-800/50">
        Counted over the events and window above. Zero rates everybody. Events entered counts
        distinct events, never entries — a re-entry is one event.
      </p>

      <div>
        <span className="mb-1.5 block text-xs text-ink-800/60">Players below the minimum</span>
        <ChoiceList
          name="rating-unqualified-display"
          value={qualification.unqualifiedDisplay}
          onChange={(unqualifiedDisplay) => onChange({ ...qualification, unqualifiedDisplay })}
          options={[
            {
              value: 'provisional',
              label: 'Shown below everybody else, marked provisional',
              detail: 'With how far short they are. The default, and the one where nobody vanishes.',
            },
            {
              value: 'hidden',
              label: 'Left off the table',
              detail:
                'Still computed and still counted — the screen says how many are being held back, because a silent exclusion is how a player disappears and nobody can explain it.',
            },
            {
              value: 'ranked',
              label: 'Ranked among everybody else',
              detail: 'For a room whose players nearly all clear the minimum anyway.',
            },
          ]}
        />
      </div>

      <IssueList issues={issues} />
    </div>
  );
}

// ---------------------------------------------------------------- summaries and copy

const VISIBILITY_COPY: Record<RatingVisibility, { label: string; detail: string }> = {
  private: {
    label: 'Just me',
    detail: 'You and a platform administrator. For a rating you are still working out.',
  },
  room: { label: 'Room staff', detail: 'Everybody who works here. Nobody outside the room.' },
  players: {
    label: 'Players with a card here',
    detail: 'Signed-in players holding a card at this room, as well as staff.',
  },
  public: {
    label: 'Anyone',
    detail: 'Including people who are not signed in. This is a public ranking of named people.',
  },
};

/** Everything in the room and nothing narrowed — the shape §5.2's reference board carries. */
function blankRatingScope(): PointsScope {
  return {
    // Open on both sides: a rating board has no date columns of its own, and the window is what
    // decides how far back it looks. §4.2 closed this on the server at Phase 3b and the mirror
    // followed at Phase 6; before that a rating scope had to carry dates wide enough never to clip
    // its window, which then truncated it years later with nothing on screen saying why.
    startDate: null,
    endDate: null,
    includeTags: [],
    excludeTags: [],
    includeEventIds: [],
    excludeEventIds: [],
    // §4.2: GPI's qualifying threshold, and the sensible default for a rating where 4 is right for
    // a leaderboard.
    minFieldSize: 32,
  };
}

function windowSummary(window: RatingWindow): string {
  if (window.type === 'rolling') return `The last ${window.lengthMonths ?? 0} months`;
  if (window.type === 'fixed') return `${window.startDate ?? '—'} to ${window.endDate ?? '—'}`;
  return 'Everything, ever';
}

function aggregationSummary(aggregation: RatingAggregationDraft): string {
  const periods = aggregation.periods.length;
  if (aggregation.mode === 'sumAll') return 'Every result, at full weight';

  const counts = aggregation.periods
    .map((p) => (p.takeAll ? 'all' : String(p.n ?? '?')))
    .join('/');

  return `${periods} × ${aggregation.periodLengthMonths ?? '?'} months · best ${counts}`;
}

function qualificationSummary(qualification: RatingQualificationDraft): string {
  const events = qualification.minEvents ?? 0;
  const cashes = qualification.minCashes ?? 0;

  if (events === 0 && cashes === 0) return 'Everybody is rated';
  return `${events} event${events === 1 ? '' : 's'}, ${cashes} cash${cashes === 1 ? '' : 'es'}`;
}

function cohortSummary(cohort: RatingCohortDraft, players: number | null): string {
  const parts = [...cohort.state, ...cohort.city, ...cohort.country].filter((v) => v.length > 0);
  const who = parts.length === 0 ? 'Every player in the room' : parts.join(', ');

  return players === null ? who : `${who} · ${players} rated`;
}

/** The board exactly as the server holds it, for the dirty check. */
function storedOf(board: RatingBoard) {
  const window = readWindow(board.window);

  return {
    name: board.name,
    description: board.description,
    pointsSystemId: board.pointsSystemId,
    pointsSystemVersion: board.pointsSystemVersionNumber,
    scope: toScopeDocument(readScope(board.scope) ?? blankRatingScope()),
    cohort: toCohortDocument(readCohort(board.cohort)),
    window: toWindowDocument(window),
    aggregation: toAggregationDocument(readAggregation(board.aggregation), window),
    qualification: toQualificationDocument(readQualification(board.qualification)),
    visibility: readVisibility(board.visibility),
    recalcMode: board.recalcMode,
    recalcCron: board.recalcCron,
  };
}
